import React, { useCallback, useState, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../lib/tenant-context';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Upload, FileText, CheckCircle2, AlertCircle, ChevronRight, ArrowLeft, Rocket, LayoutDashboard, UtensilsCrossed, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { parseCsv, validateCsv, structureMenu, importMenu } from '../../lib/csv-import';
import type { ValidationResult, StructuredMenu, ImportResult } from '../../lib/csv-import';
type Step = 'upload' | 'validate' | 'preview' | 'confirm' | 'success';
const STEPS: {
  key: Step;
  label: string;
}[] = [{
  key: 'upload',
  label: 'Subir'
}, {
  key: 'validate',
  label: 'Validar'
}, {
  key: 'preview',
  label: 'Vista previa'
}, {
  key: 'confirm',
  label: 'Confirmar'
}, {
  key: 'success',
  label: 'Listo'
}];
export function ImportPage() {
  const {
    restaurant,
    role,
    signOut
  } = useTenant();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState<string | null>(null);
  const [rawText, setRawText] = useState<string | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [menu, setMenu] = useState<StructuredMenu | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // ─── File handling ─────────────────────────────────────────
  const processFile = useCallback((file: File) => {
    if (!file.name.endsWith('.csv')) {
      alert('Solo se aceptan archivos .csv');
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      setRawText(text);
      // Auto-advance to validation
      const rawRows = parseCsv(text);
      const validationResult = validateCsv(rawRows);
      setValidation(validationResult);
      setStep('validate');
    };
    reader.readAsText(file);
  }, []);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };
  // ─── Step transitions ──────────────────────────────────────
  const goToPreview = () => {
    if (!validation) return;
    const structured = structureMenu(validation.validRows);
    setMenu(structured);
    setStep('preview');
  };
  const goToConfirm = () => setStep('confirm');
  const handleImport = async () => {
    if (!menu || !restaurant) return;
    setImporting(true);
    const importResult = await importMenu(restaurant.id, menu);
    setResult(importResult);
    setImporting(false);
    if (importResult.success) {
      setStep('success');
    }
  };
  const reset = () => {
    setStep('upload');
    setFileName(null);
    setRawText(null);
    setValidation(null);
    setMenu(null);
    setResult(null);
  };
  // ─── Step index for progress ───────────────────────────────
  const currentStepIdx = STEPS.findIndex(s => s.key === step);
  // ─── Render ────────────────────────────────────────────────
  return <AdminLayout restaurant={restaurant} role={role} onSignOut={signOut} data-id="element-3217">
      <div className="max-w-3xl mx-auto px-4 py-8" data-id="element-3218">
        {/* Header */}
        <div className="mb-8" data-id="element-3219">
          <button onClick={() => navigate(`/admin/${restaurant.slug}/menu`)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-4" data-id="element-3220">
            <ArrowLeft className="w-4 h-4" data-id="element-3221" />
            Volver al menú
          </button>
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-3222">
            Importar menú desde CSV
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-3223">
            Sube un archivo CSV con tu menú y lo crearemos automáticamente.
          </p>
        </div>

        {/* Progress bar */}
        <div className="flex items-center gap-1 mb-10" data-id="element-3224">
          {STEPS.map((s, idx) => <Fragment key={s.key} data-id="element-3225">
              <div className="flex items-center gap-2" data-id="element-3226">
                <div className={cn('w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors', idx < currentStepIdx ? 'bg-green-500 text-white' : idx === currentStepIdx ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-400')} data-id="element-3227">
                  {idx < currentStepIdx ? <CheckCircle2 className="w-4 h-4" data-id="element-3228" /> : idx + 1}
                </div>
                <span className={cn('text-xs font-medium hidden sm:block', idx <= currentStepIdx ? 'text-gray-900' : 'text-gray-400')} data-id="element-3229">
                  {s.label}
                </span>
              </div>
              {idx < STEPS.length - 1 && <div className={cn('flex-1 h-0.5 rounded-full mx-1', idx < currentStepIdx ? 'bg-green-500' : 'bg-gray-100')} data-id="element-3230" />}
            </Fragment>)}
        </div>

        {/* ═══ STEP 1: UPLOAD ═══ */}
        {step === 'upload' && <div onDragOver={e => {
        e.preventDefault();
        setDragOver(true);
      }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} className={cn('border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all', dragOver ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/50')} data-id="element-3231">
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileChange} className="hidden" data-id="element-3232" />
            <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-5" data-id="element-3233">
              <Upload className="w-6 h-6 text-gray-400" data-id="element-3234" />
            </div>
            <p className="text-base font-semibold text-gray-900 mb-1" data-id="element-3235">
              Arrastra tu archivo CSV aquí
            </p>
            <p className="text-sm text-gray-500 mb-6" data-id="element-3236">
              o haz clic para seleccionar
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg" data-id="element-3237">
              <FileText className="w-4 h-4" data-id="element-3238" />
              Seleccionar archivo
            </div>

            <div className="mt-8 pt-6 border-t border-gray-100" data-id="element-3239">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3" data-id="element-3240">
                Columnas requeridas
              </p>
              <div className="flex flex-wrap justify-center gap-2" data-id="element-3241">
                <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-mono rounded" data-id="element-3242">
                  category_name
                </span>
                <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-mono rounded" data-id="element-3243">
                  item_name
                </span>
                <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-mono rounded" data-id="element-3244">
                  price
                </span>
                <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-mono rounded" data-id="element-3245">
                  station_code
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-2" data-id="element-3246">
                Opcionales: description, option_name, option_price
              </p>
            </div>
          </div>}

        {/* ═══ STEP 2: VALIDATE ═══ */}
        {step === 'validate' && validation && <div className="space-y-6" data-id="element-3247">
            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-4" data-id="element-3248">
              <div className="bg-green-50 border border-green-200 rounded-xl p-5" data-id="element-3249">
                <div className="flex items-center gap-2 mb-1" data-id="element-3250">
                  <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-3251" />
                  <span className="text-sm font-semibold text-green-800" data-id="element-3252">
                    Filas válidas
                  </span>
                </div>
                <p className="text-3xl font-black text-green-700" data-id="element-3253">
                  {validation.validRows.length}
                </p>
              </div>
              <div className={cn('border rounded-xl p-5', validation.invalidRows.length > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200')} data-id="element-3254">
                <div className="flex items-center gap-2 mb-1" data-id="element-3255">
                  <AlertCircle className={cn('w-5 h-5', validation.invalidRows.length > 0 ? 'text-red-500' : 'text-gray-400')} data-id="element-3256" />
                  <span className={cn('text-sm font-semibold', validation.invalidRows.length > 0 ? 'text-red-800' : 'text-gray-600')} data-id="element-3257">
                    Filas con errores
                  </span>
                </div>
                <p className={cn('text-3xl font-black', validation.invalidRows.length > 0 ? 'text-red-600' : 'text-gray-400')} data-id="element-3258">
                  {validation.invalidRows.length}
                </p>
              </div>
            </div>

            {/* Invalid rows detail */}
            {validation.invalidRows.length > 0 && <div className="bg-white border border-red-200 rounded-xl overflow-hidden" data-id="element-3259">
                <div className="px-5 py-3 bg-red-50 border-b border-red-200" data-id="element-3260">
                  <p className="text-sm font-semibold text-red-800" data-id="element-3261">
                    Errores encontrados
                  </p>
                </div>
                <div className="max-h-60 overflow-y-auto divide-y divide-red-100" data-id="element-3262">
                  {validation.invalidRows.map(row => <div key={row.row} className="px-5 py-3" data-id="element-3263">
                      <div className="flex items-center gap-2 mb-1" data-id="element-3264">
                        <span className="text-xs font-bold text-red-500 bg-red-100 px-2 py-0.5 rounded" data-id="element-3265">
                          Fila {row.row}
                        </span>
                      </div>
                      <ul className="space-y-0.5" data-id="element-3266">
                        {row.errors.map((err, i) => <li key={i} className="text-xs text-red-700 flex items-center gap-1" data-id="element-3267">
                            <span data-id="element-3268">•</span> {err}
                          </li>)}
                      </ul>
                    </div>)}
                </div>
              </div>}

            {/* File info */}
            {fileName && <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 border border-gray-200" data-id="element-3269">
                <FileText className="w-5 h-5 text-gray-400" data-id="element-3270" />
                <span className="text-sm text-gray-700 font-medium flex-1 truncate" data-id="element-3271">
                  {fileName}
                </span>
                <button onClick={reset} className="text-xs text-gray-400 hover:text-gray-600 transition-colors" data-id="element-3272">
                  Cambiar archivo
                </button>
              </div>}

            {/* Actions */}
            <div className="flex gap-3" data-id="element-3273">
              <button onClick={reset} className="px-5 py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" data-id="element-3274">
                Volver
              </button>
              <button onClick={goToPreview} disabled={validation.validRows.length === 0} className={cn('flex-1 flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold rounded-xl transition-colors', validation.validRows.length > 0 ? 'bg-gray-900 text-white hover:bg-gray-800' : 'bg-gray-100 text-gray-400 cursor-not-allowed')} data-id="element-3275">
                Continuar con {validation.validRows.length} filas
                <ChevronRight className="w-4 h-4" data-id="element-3276" />
              </button>
            </div>
          </div>}

        {/* ═══ STEP 3: PREVIEW ═══ */}
        {step === 'preview' && menu && <div className="space-y-6" data-id="element-3277">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3" data-id="element-3278">
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-center" data-id="element-3279">
                <p className="text-2xl font-black text-gray-900" data-id="element-3280">
                  {menu.totalCategories}
                </p>
                <p className="text-xs text-gray-500 font-medium mt-0.5" data-id="element-3281">
                  Categorías
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-center" data-id="element-3282">
                <p className="text-2xl font-black text-gray-900" data-id="element-3283">
                  {menu.totalItems}
                </p>
                <p className="text-xs text-gray-500 font-medium mt-0.5" data-id="element-3284">
                  Productos
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-center" data-id="element-3285">
                <p className="text-2xl font-black text-gray-900" data-id="element-3286">
                  {menu.totalOptions}
                </p>
                <p className="text-xs text-gray-500 font-medium mt-0.5" data-id="element-3287">
                  Opciones
                </p>
              </div>
            </div>

            {/* Category breakdown */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-3288">
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-200" data-id="element-3289">
                <p className="text-sm font-semibold text-gray-900" data-id="element-3290">
                  Vista previa del menú
                </p>
              </div>
              <div className="divide-y divide-gray-100 max-h-[28rem] overflow-y-auto" data-id="element-3291">
                {menu.categories.map(cat => <div key={cat.name} className="px-5 py-4" data-id="element-3292">
                    <div className="flex items-center justify-between mb-3" data-id="element-3293">
                      <h3 className="text-sm font-bold text-gray-900" data-id="element-3294">
                        {cat.name}
                      </h3>
                      <span className="text-xs text-gray-400 font-medium" data-id="element-3295">
                        {cat.items.length}{' '}
                        {cat.items.length === 1 ? 'producto' : 'productos'}
                      </span>
                    </div>
                    <div className="space-y-2" data-id="element-3296">
                      {cat.items.map(item => <div key={`${cat.name}-${item.name}`} className="flex items-start justify-between gap-4 py-1.5" data-id="element-3297">
                          <div className="flex-1 min-w-0" data-id="element-3298">
                            <p className="text-sm text-gray-800 font-medium truncate" data-id="element-3299">
                              {item.name}
                            </p>
                            {item.description && <p className="text-xs text-gray-400 truncate" data-id="element-3300">
                                {item.description}
                              </p>}
                            {item.options.length > 0 && <div className="flex flex-wrap gap-1 mt-1" data-id="element-3301">
                                {item.options.map(opt => <span key={opt.name} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-medium" data-id="element-3302">
                                    {opt.name}
                                    {opt.price > 0 && ` +₡${opt.price}`}
                                  </span>)}
                              </div>}
                          </div>
                          <div className="text-right shrink-0" data-id="element-3303">
                            <p className="text-sm font-bold text-gray-900" data-id="element-3304">
                              ₡{item.price.toLocaleString()}
                            </p>
                            <p className="text-[10px] text-gray-400 font-mono" data-id="element-3305">
                              {item.station_code}
                            </p>
                          </div>
                        </div>)}
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3" data-id="element-3306">
              <button onClick={() => setStep('validate')} className="px-5 py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" data-id="element-3307">
                Volver
              </button>
              <button onClick={goToConfirm} className="flex-1 flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors" data-id="element-3308">
                Todo se ve bien
                <ChevronRight className="w-4 h-4" data-id="element-3309" />
              </button>
            </div>
          </div>}

        {/* ═══ STEP 4: CONFIRM ═══ */}
        {step === 'confirm' && menu && <div className="space-y-6" data-id="element-3310">
            <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center" data-id="element-3311">
              <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-5" data-id="element-3312">
                <span className="text-3xl" data-id="element-3313">🚀</span>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-3314">
                ¿Crear el menú?
              </h2>
              <p className="text-sm text-gray-500 max-w-sm mx-auto mb-6" data-id="element-3315">
                Se crearán {menu.totalCategories} categorías, {menu.totalItems}{' '}
                productos
                {menu.totalOptions > 0 && ` y ${menu.totalOptions} opciones`} en{' '}
                <strong data-id="element-3316">{restaurant.name}</strong>.
              </p>

              {result && !result.success && <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-left" data-id="element-3317">
                  <div className="flex items-center gap-2 mb-1" data-id="element-3318">
                    <AlertCircle className="w-4 h-4 text-red-500" data-id="element-3319" />
                    <span className="text-sm font-semibold text-red-800" data-id="element-3320">
                      Error durante la importación
                    </span>
                  </div>
                  <p className="text-xs text-red-700" data-id="element-3321">{result.error}</p>
                  <p className="text-xs text-red-500 mt-1" data-id="element-3322">
                    Se crearon {result.categoriesCreated} categorías,{' '}
                    {result.itemsCreated} productos y {result.optionsCreated}{' '}
                    opciones antes del error.
                  </p>
                </div>}

              <div className="flex gap-3" data-id="element-3323">
                <button onClick={() => setStep('preview')} disabled={importing} className="px-5 py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" data-id="element-3324">
                  Volver
                </button>
                <button onClick={handleImport} disabled={importing} className={cn('flex-1 flex items-center justify-center gap-2 px-5 py-3.5 text-sm font-bold rounded-xl transition-colors', importing ? 'bg-gray-300 text-gray-500 cursor-wait' : 'bg-green-600 text-white hover:bg-green-700')} data-id="element-3325">
                  {importing ? <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" data-id="element-3326">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-3327" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-3328" />
                      </svg>
                      Creando menú...
                    </> : <>
                      <Rocket className="w-4 h-4" data-id="element-3329" />
                      Crear menú
                    </>}
                </button>
              </div>
            </div>
          </div>}

        {/* ═══ STEP 5: SUCCESS ═══ */}
        {step === 'success' && result && <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center" data-id="element-3330">
            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-3331">
              <span className="text-4xl" data-id="element-3332">🚀</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3333">
              ¡Tu menú está listo!
            </h2>
            <p className="text-sm text-gray-500 max-w-sm mx-auto mb-2" data-id="element-3334">
              Se crearon exitosamente:
            </p>
            <div className="flex justify-center gap-6 mb-8" data-id="element-3335">
              <div className="text-center" data-id="element-3336">
                <p className="text-2xl font-black text-green-600" data-id="element-3337">
                  {result.categoriesCreated}
                </p>
                <p className="text-xs text-gray-500" data-id="element-3338">categorías</p>
              </div>
              <div className="text-center" data-id="element-3339">
                <p className="text-2xl font-black text-green-600" data-id="element-3340">
                  {result.itemsCreated}
                </p>
                <p className="text-xs text-gray-500" data-id="element-3341">productos</p>
              </div>
              {result.optionsCreated > 0 && <div className="text-center" data-id="element-3342">
                  <p className="text-2xl font-black text-green-600" data-id="element-3343">
                    {result.optionsCreated}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-3344">opciones</p>
                </div>}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 max-w-sm mx-auto" data-id="element-3345">
              <button onClick={() => navigate(`/admin/${restaurant.slug}/menu`)} className="flex-1 flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors" data-id="element-3346">
                <UtensilsCrossed className="w-4 h-4" data-id="element-3347" />
                Ver menú
              </button>
              <button onClick={() => navigate(`/admin/${restaurant.slug}/dashboard`)} className="flex-1 flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" data-id="element-3348">
                <LayoutDashboard className="w-4 h-4" data-id="element-3349" />
                Dashboard
              </button>
            </div>
          </div>}
      </div>
    </AdminLayout>;
}